/**
 * Custom postback domain — data layer (FS-675).
 *
 * Thin typed wrapper over `apiClient` for the agency-branded S2S callback
 * domain endpoints:
 *
 *   GET    /api/v1/custom-postback-domain           — listDomains
 *   POST   /api/v1/custom-postback-domain           — createDomain
 *   DELETE /api/v1/custom-postback-domain/:id       — deleteDomain
 *   POST   /api/v1/custom-postback-domain/:id/verify — verifyDomain
 *
 * Pure helpers (validators, formatters, query keys) live here too so they
 * can be unit-tested without a DOM or TanStack context.
 */
import { apiClient } from "../../api-client";

// ---------------------------------------------------------------------------
// Row shape — mirror the server response exactly.
// ---------------------------------------------------------------------------

export interface CustomPostbackDomain {
	id: string;
	tenant_id: string;
	domain: string;
	verified: number;
	active: number;
	created_at: number;
	updated_at: number;
	deleted_at: number | null;
}

export interface ListDomainsResponse {
	domains: CustomPostbackDomain[];
}

export interface DomainResponse {
	domain: CustomPostbackDomain;
}

export interface DeleteResponse {
	ok: true;
	id: string;
}

// ---------------------------------------------------------------------------
// Validators — mirror the server-side `normalizeDomain` so the UI can
// short-circuit obvious errors before the network round-trip.
// ---------------------------------------------------------------------------

const FQDN_RE =
	/^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/i;
const RESERVED_SUFFIX = "trackerity.com";

/**
 * Normalise + validate an FQDN. Returns the canonical lowercased form
 * (trailing-dot stripped) or `null` if the shape check fails or the domain
 * lives inside the reserved suffix.
 */
export function normalizeDomain(raw: unknown): string | null {
	if (typeof raw !== "string") return null;
	const trimmed = raw.trim().toLowerCase().replace(/\.$/, "");
	if (!trimmed) return null;
	if (!FQDN_RE.test(trimmed)) return null;
	if (trimmed === RESERVED_SUFFIX || trimmed.endsWith(`.${RESERVED_SUFFIX}`)) {
		return null;
	}
	return trimmed;
}

// ---------------------------------------------------------------------------
// Formatters — pure, DOM-free.
// ---------------------------------------------------------------------------

export function formatUnixSec(unixSec: number | null | undefined): string {
	if (unixSec == null) return "—";
	if (!Number.isFinite(unixSec) || unixSec <= 0) return "—";
	return new Date(unixSec * 1000).toLocaleString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "numeric",
		minute: "2-digit",
	});
}

export type DomainStatus = "verified" | "pending" | "inactive";

export function statusOf(d: CustomPostbackDomain): DomainStatus {
	if (d.active !== 1) return "inactive";
	if (d.verified === 1) return "verified";
	return "pending";
}

export function statusLabel(s: DomainStatus): string {
	if (s === "verified") return "Verified";
	if (s === "inactive") return "Inactive";
	return "Pending";
}

/**
 * Emerald pill for verified rows; zinc for pending; rose for inactive.
 * Kept DOM-free so the test suite can assert class composition.
 */
export function statusBadgeClass(s: DomainStatus): string {
	if (s === "verified") {
		return "inline-flex items-center gap-1 rounded-[999px] bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200";
	}
	if (s === "inactive") {
		return "inline-flex items-center gap-1 rounded-[999px] bg-rose-50 px-2 py-0.5 text-xs font-medium text-rose-700 ring-1 ring-inset ring-rose-200";
	}
	return "inline-flex items-center gap-1 rounded-[999px] bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-700 ring-1 ring-inset ring-zinc-200";
}

// ---------------------------------------------------------------------------
// Query keys — grouped for ergonomic invalidation.
// ---------------------------------------------------------------------------

export const customPostbackDomainQueryKeys = {
	all: ["custom-postback-domain"] as const,
	list: () => [...customPostbackDomainQueryKeys.all, "list"] as const,
};

// ---------------------------------------------------------------------------
// Remote bindings — thin `apiClient` shims.
// ---------------------------------------------------------------------------

const BASE = "/custom-postback-domain";

export async function listDomains(): Promise<CustomPostbackDomain[]> {
	const res = await apiClient.get<ListDomainsResponse>(BASE);
	return res.domains;
}

export async function createDomain(
	domain: string,
): Promise<CustomPostbackDomain> {
	const res = await apiClient.post<DomainResponse>(BASE, { domain });
	return res.domain;
}

export async function deleteDomain(id: string): Promise<DeleteResponse> {
	return apiClient.delete<DeleteResponse>(
		`${BASE}/${encodeURIComponent(id)}`,
	);
}

export async function verifyDomain(
	id: string,
): Promise<CustomPostbackDomain> {
	const res = await apiClient.post<DomainResponse>(
		`${BASE}/${encodeURIComponent(id)}/verify`,
	);
	return res.domain;
}
